import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import HomeHeader from './components/HomeHeader';
import HeroSection from './components/HeroSection';
import SocialProofBar from './components/SocialProofBar';
import FeaturesShowcase from './components/FeaturesShowcase';
import DetailedFeaturesGrid from './components/DetailedFeaturesGrid';
import WorkflowSection from './components/WorkflowSection';
import TestimonialsSection from './components/TestimonialsSection';
import PricingSection from './components/PricingSection';
import ResponseCapSection from './components/ResponseCapSection';
import FAQSection from './components/FAQSection';

const HomePage = () => {
  const schemaSoftwareApplication = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "WebForms.biz",
    "url": "https://webforms.biz",
    "applicationCategory": "BusinessApplication",
    "applicationSubCategory": "Form Builder",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "lowPrice": "0",
      "highPrice": "169",
      "offerCount": "4"
    }
  };

  const schemaWebSite = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "WebForms.biz",
    "url": "https://webforms.biz",
    "description": "Take control of your web forms. Build, publish, and optimize forms that convert."
  };

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>WebForms.biz – Take Control of Your Web Forms | Professional Form Builder</title>
        <meta name="description" content="Build, test, and publish forms with analytics, payments, integrations, and A/B testing. Start free with WebForms.biz." />
        <meta name="keywords" content="form builder, web forms, drag and drop form builder, form analytics, payment forms, white label forms, A/B testing forms" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://webforms.biz/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://webforms.biz/" />
        <meta property="og:title" content="WebForms.biz – Take Control of Your Web Forms" />
        <meta property="og:description" content="Build, test, and publish forms with analytics, payments, integrations, and A/B testing. Start free." />
        <meta property="og:image" content="https://webforms.biz/assets/images/web-forms-logo-1769800970615.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="WebForms.biz – Take Control of Your Web Forms" />
        <meta name="twitter:description" content="Build, test, and publish forms with analytics, payments, integrations, and A/B testing. Start free." />
        <meta name="theme-color" content="#0e7572" />
        <script type="application/ld+json">{JSON.stringify(schemaWebSite)}</script>
        <script type="application/ld+json">{JSON.stringify(schemaSoftwareApplication)}</script>
      </Helmet>
      {/* Header */}
      <HomeHeader />
      <main>
        {/* Hero — dark, full-screen */}
        <HeroSection />

        {/* Social proof bar — stats + integrations */}
        <SocialProofBar />

        {/* Response-cap differentiation */}
        <ResponseCapSection />

        {/* Features bento grid */}
        <FeaturesShowcase />

        {/* Detailed alternating features */}
        <DetailedFeaturesGrid />

        {/* How it works */}
        <WorkflowSection />

        {/* Testimonials */}
        <TestimonialsSection />

        {/* FAQ */}
        <FAQSection />

        {/* Pricing */}
        <PricingSection />

        {/* Footer CTA */}
        <section className="py-24 bg-white border-t border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-xs font-semibold text-teal-700 mb-6">
              <Icon name="Rocket" size={13} />
              <span>Get started today</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-5 leading-tight">
              Your forms. Your rules.<br />
              <span className="text-[#0e7572]">Your results.</span>
            </h2>
            <p className="text-lg text-gray-500 mb-10 max-w-2xl mx-auto leading-relaxed">
              Stop losing leads to clunky forms. Give your team one place to build, publish, and improve every form.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Link
                to="/authentication"
                className="flex items-center gap-2 px-8 py-4 bg-[#0e7572] hover:bg-[#0a5f5c] text-white font-semibold text-base rounded-xl transition-all duration-200 shadow-lg shadow-teal-100 hover:shadow-xl hover:-translate-y-0.5"
              >
                <span>Start building for free</span>
                <Icon name="ArrowRight" size={18} />
              </Link>
              <Link
                to="/templates-gallery"
                className="flex items-center gap-2 px-8 py-4 border-2 border-gray-200 hover:border-gray-300 text-gray-700 hover:text-gray-900 font-semibold text-base rounded-xl transition-all duration-200"
              >
                <Icon name="LayoutTemplate" size={18} />
                <span>Browse templates</span>
              </Link>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
              {['Free plan available', 'Plan limits shown up front', 'Publish without code', 'Support available']?.map((item) => (
                <div key={item} className="flex items-center gap-1.5">
                  <Icon name="Check" size={14} className="text-teal-500" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default HomePage;